import React, { useState, useEffect, useRef, Dispatch, SetStateAction } from "react";
import WebViewer, { WebViewerInstance } from "@pdftron/webviewer";
import { RecoveryCodeInfo, AppConfigurationInfo } from "@preveil-api";
import { AppConfiguration, dayjs, Message, PDFTRON_WEBVIEWER_OPTIONS, SettingsErrorMessages, useAppDispatch, FileTemplates } from "src/common";
import { uiActions } from "src/store";
import { saveAs } from "file-saver";
import { toDataURL } from "qrcode";

type AllProps = {
  config: AppConfigurationInfo;
  recovery_code: { backup_id: string; text_encoding?: string };
  user_id: string;
  getBackup: Function;
  setDownloadRecoveryCode: Dispatch<SetStateAction<RecoveryCodeInfo | undefined>>;
};

function PDFGeneratorComponent(props: AllProps) {
  const { config, recovery_code, user_id, getBackup, setDownloadRecoveryCode } = props;
  const backup_prefix = recovery_code.backup_id.substring(0, 6);
  const file_name = `recovery-file-${backup_prefix}.pdf`;
  const recovery_url = `${AppConfiguration.buildForWeb() ? config.local_web_server : config.local_app_server}/backup-recovery`;
  const dispatch = useAppDispatch();
  const [instance, setInstance] = useState<WebViewerInstance | undefined>(undefined);
  const viewer = useRef<HTMLDivElement>(null);

  /* Description: on initial load, get the qr code and instantiate a new instance of web viewer.
  We use a template for the initial doc which we will then modify with the current users backup information */
  useEffect(() => {
    initWebViewer();
  }, []);

  /* Description: cleanup function, disposes the current documents data */
  useEffect(() => {
    return () => {
      instance?.Core.documentViewer.dispose();
    };
  }, [instance]);

  // Description: initialize the web viewer
  async function initWebViewer() {
    const qrcode = await getQRCode();
    if (!!qrcode && !!viewer) {
      const _instance = await WebViewer(
        Object.assign({}, PDFTRON_WEBVIEWER_OPTIONS, {
          initialDoc: FileTemplates.pdf_generator,
        }),
        viewer.current as HTMLDivElement,
      );

      const { documentViewer, PDFNet } = _instance.Core;
      // NOTE: Disable js embeds for CSP 
      AppConfiguration.buildForWeb() && _instance.Core.disableEmbeddedJavaScript();
      documentViewer.addEventListener("documentLoaded", async () => {
        await PDFNet.runWithCleanup(async () => {
          await modifyPDF(_instance, qrcode);
        }, PDFTRON_WEBVIEWER_OPTIONS.licenseKey);
      });
      setInstance(_instance);
    }
  }

  /* Description: Notify the store that the backup pdf file has been downloaded and call the getBackup function again. */
  function downloadComplete() {
    setDownloadRecoveryCode(undefined);
    getBackup();
  };

  /* Description: create and modify the initial template with the users backup info.
     NOTE: DA-8023 — The recovery_code field value is set via PDFNet XFDF merge + flatten
     instead of the annotation manager's field.setValue(). This bypasses a WebViewer 11.10+
     regression where multiline fields with long unbroken strings render as a single-character
     column. By merging a value-only XFDF (no <annotations>) and flattening at the PDFNet level,
     we get a single clean text layer with correct wrapping. */
  async function modifyPDF(instance: WebViewerInstance, qrcode: string | undefined) {
    if (!!qrcode) {
      const { documentViewer, PDFNet } = instance.Core;
      await PDFNet.initialize();
      const doc = await documentViewer.getDocument().getPDFDoc();

      try {
        // lock the document before a write operation
        await doc.lock();

        const replacer = await PDFNet.ContentReplacer.create();
        const page = await doc.getPage(1);
        const current_date = dayjs().local().format("MM/DD/YYYY");

        await replacer.addString("backup_prefix", backup_prefix);
        await replacer.addString("email_address", user_id);
        await replacer.addString("create_date", current_date);
        await replacer.addString("recovery_link", recovery_url);
        await replacer.process(page);

        // Insert QR code image
        const builder = await PDFNet.ElementBuilder.create();
        const writer = await PDFNet.ElementWriter.create();
        writer.beginOnPage(page);
        const img = await PDFNet.Image.createFromURL(doc, qrcode);
        const element = await builder.createImageScaled(img, 200, 50, 200, 200);
        writer.writePlacedElement(element);
        writer.end();

        // Set recovery_code field value via FDF merge (value only, no widget annotations)
        if (!!recovery_code.text_encoding) {
          const xfdf = `<?xml version="1.0" encoding="UTF-8"?><xfdf xmlns="http://ns.adobe.com/xfdf/"><fields><field name="recovery_code"><value>${recovery_code.text_encoding}</value></field></fields></xfdf>`;
          const fdf_doc = await PDFNet.FDFDoc.createFromXFDF(xfdf);
          await doc.fdfMerge(fdf_doc);
        }

        // Flatten form fields to static content and save
        await doc.flattenAnnotations(true);
        const buf = await doc.saveMemoryBuffer(PDFNet.SDFDoc.SaveOptions.e_linearized);

        const blob = new Blob([new Uint8Array(buf)], { type: "application/pdf" });
        saveAs(blob, file_name);
        downloadComplete();
      } catch (e: unknown) {
        console.error(e);
        dispatch(uiActions.handleRequestErrors(new Message(SettingsErrorMessages.error_generating_pdf)));
        downloadComplete();
      }
    } else {
      dispatch(uiActions.handleRequestErrors(new Message(SettingsErrorMessages.error_generating_qr)));
      downloadComplete();
    }
  }

  /* Description: gets a Data URI containing a representation of the QR Code image for the users recovery code. */
  async function getQRCode(): Promise<string | undefined> {
    if (!!recovery_code.text_encoding) {
      return await toDataURL(recovery_code.text_encoding, {
        errorCorrectionLevel: "H",
        type: "image/jpeg",
        width: 500,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      })
        .then((url) => {
          return url;
        })
        .catch(() => {
          return undefined;
        });
    } else {
      return undefined;
    }
  }

  return (
    <div>
      <div className="opacity-0" ref={viewer}></div>
    </div>
  );
}

export default React.memo(PDFGeneratorComponent);
